/** Closed admission for an original Resource editor command. Validators never
 * normalize or replace captured bytes; the existing editor adapters serialize
 * new intentions, and the server remains the content/authority boundary. */
import { z } from 'zod';
import { sceneFromOnDisk } from './canvas/board-schema';
import { objectFromOnDisk } from './canvas/telestrator-schema';
import { feedbackProseLength } from './feedbackAuthoringContinuation';
const uuid=z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
function closed(value: unknown, keys: string[]): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    && Object.keys(value).every(key => keys.includes(key));
}
/** Structural boundary only, not a second content normalizer. Keep wire bytes.
 * Retain the exact wire value after this bounded structural check. */
function boardWire(value: unknown): boolean {
  if (!closed(value, ['version','pitch_kind','orientation','game_format','formation','tokens','shapes'])
    || !Array.isArray(value.tokens) || !Array.isArray(value.shapes)
    || !sceneFromOnDisk(value).ok) return false;
  const geometries: Record<string, string[]> = { arrow:['x1','y1','x2','y2'], line:['x1','y1','x2','y2'],
    zone:['x','y','w','h','text'], label:['x','y','text'], freehand:['points'] };
  return value.tokens.every(token => closed(token,['kind','id','x','y','label','player_id','color']))
    && value.shapes.every(shape => {
      if (!closed(shape,['kind','id','color','stroke_width', ...Object.values(geometries).flat()])
        || typeof shape.kind !== 'string' || !geometries[shape.kind]
        || !closed(shape,['kind','id','color','stroke_width',...geometries[shape.kind]])) return false;
      return !('points' in shape) || Array.isArray(shape.points)
        && shape.points.length <= 200 && shape.points.every(point => closed(point,['x','y']));
    });
}

const unit=(value:unknown)=>typeof value==='number'&&Number.isFinite(value)&&value>=0&&value<=1;
function point(value:unknown,anchor=false):boolean {
  return closed(value,anchor?['x','y','player_id','label']:['x','y'])&&unit(value.x)&&unit(value.y)
    && (!('player_id' in value)||value.player_id==null||typeof value.player_id==='string'&&value.player_id.length<=64)
    && (!('label' in value)||typeof value.label==='string'&&value.label.length<=40);
}
export function resourceDrawingWire(value:unknown):boolean {
  if(value===null)return true;
  if(!closed(value,['version','objects','strokes']))return false;
  const version='version' in value?value.version:1, name=version===1?'strokes':version===2?'objects':null;
  if(!name||!closed(value,['version',name]))return false;
  // The server and existing loader accept omitted collections as empty.
  // Preserve their original representation; explicit null remains invalid.
  const items=name in value?value[name]:[];
  if(!Array.isArray(items)||items.length>120)return false;
  const geometries:Record<string,string[]>={freehand:['points'],arrow:['x1','y1','x2','y2'],line:['x1','y1','x2','y2'],
    circle:['x','y','w','h'],zone:['x','y','w','h','text'],label:['x','y','text'],spotlight:['x','y','w','h'],
    dim:['opacity'],formation:['anchors','hull_points']};
  let total=0;
  return items.every((item:unknown)=>{
    if(!closed(item,version===1?['color','width','points']:['type','id','color','width',...Object.values(geometries).flat()]))return false;
    const kind=version===1?'freehand':item.type;
    if(typeof kind!=='string'||!geometries[kind])return false;
    if(version===2&&!closed(item,['type','id',...(kind==='dim'?[]:['color','width']),...geometries[kind]]))return false;
    if('id' in item&&(typeof item.id!=='string'||item.id.length>128))return false;
    for(const key of ['points','anchors','hull_points'])if(key in item){
      const values=item[key];if(!Array.isArray(values)||values.length>(key==='points'?800:16)
        ||!values.every(p=>point(p,key==='anchors')))return false;
      if(key==='points'){total+=values.length;if(total>4000)return false;}
    }
    return objectFromOnDisk(version===1?{...item,type:'freehand'}:item)!==null;
  });
}
const recording={schema_version:z.literal(1),recording_id:uuid,recording_generation:z.string().min(1).max(128),
  match_id:z.string().min(1).max(120),slot:z.enum(['full','first_half','second_half','extra_time','penalty_shootout']),
  drawing:z.custom<Record<string,unknown>|null>(resourceDrawingWire).optional()};
const time=z.number().finite().nonnegative();
const common={schema_version:z.literal('resource.v1'),operation_id:uuid,
  expected_version:z.number().int().min(0).max(2147483647),label:z.string().refine(value=>feedbackProseLength(value)<=160).optional()};
export const resourceSaveBodySchema=z.discriminatedUnion('kind',[
  z.object({...common,kind:z.literal('tactical_board'),payload:z.object({schema_version:z.literal(1),scene:z.custom<Record<string,unknown>>(boardWire)}).strict()}).strict(),
  z.object({...common,kind:z.literal('moment'),payload:z.object({...recording,timestamp_seconds:time}).strict()}).strict(),
  z.object({...common,kind:z.literal('clip'),payload:z.object({...recording,start_seconds:time,end_seconds:time,
    derived_from:z.object({resource_id:uuid,revision_id:uuid}).strict().nullable().optional()}).strict()
    .refine(v=>v.end_seconds>v.start_seconds&&v.end_seconds-v.start_seconds<=120)}).strict(),
]);
